import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '@/store/store';
import {
  setCredentials,
  clearCredentials,
  setAuthLoading,
  setAuthError,
  selectIsAuthenticated,
  selectCurrentUser,
  selectAuthLoading,
  selectAuthError,
} from '@/store/slices/authSlice';
import { setAccessToken, setRefreshToken, clearTokens, parseTokenPayload } from '@/utils/jwt';
import type { LoginCredentials, User } from '@/types';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => selectIsAuthenticated(state));
  const user = useSelector((state: RootState) => selectCurrentUser(state));
  const loading = useSelector((state: RootState) => selectAuthLoading(state));
  const error = useSelector((state: RootState) => selectAuthError(state));

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        dispatch(setAuthLoading(true));
        dispatch(setAuthError(null));

        const response = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        });

        if (!response.ok) {
          const errorData = (await response.json()) as { error?: string };
          throw new Error(errorData.error ?? 'Login failed');
        }

        const data = (await response.json()) as {
          access_token: string;
          refresh_token: string;
          user: { email: string; roles: string[] };
        };

        // Store tokens
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);

        // Extract user info from JWT claims + login response
        const claims = parseTokenPayload(data.access_token);
        const userObj: User = {
          id: typeof claims?.sub === 'string' ? claims.sub : '',
          email: data.user.email,
          displayName: typeof claims?.name === 'string' ? claims.name : data.user.email,
          roles: data.user.roles,
        };

        dispatch(
          setCredentials({
            user: userObj,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          }),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        dispatch(setAuthError(message));
      } finally {
        dispatch(setAuthLoading(false));
      }
    },
    [dispatch],
  );

  const logout = useCallback(async () => {
    try {
      await fetch('/api/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error: unknown) {
      console.error('Logout failed:', error);
    } finally {
      clearTokens();
      dispatch(clearCredentials());
    }
  }, [dispatch]);

  return {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
  };
}
